import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ButtonCustom from 'components/ButtonCustom';
import CustomDatePicker from 'components/CustomDatePicker';
import CustomTableCell from 'components/CustomTableCell';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import StatusChip from 'components/StatusChip';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import AuthContext from 'context/AuthContext';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from "react-query";
import { useNavigate } from 'react-router-dom';
import { getAllJobAnalytics, getJobQueueData, getWorkerStats } from "services/admin-organizations";
import { JobAnalytics, Worker } from 'types/admin';

import ButtonIcon from 'components/ButtonIcon';
import { generateViewerUrl, killRevision } from 'services/revisions';
import { RevisionStatus } from 'utils/contracts';

interface Column {
  id: string;
  title: string;
}

const WorkerCard: React.FC<{ worker: Worker }> = ({ worker }) => {
  const { t } = useTranslation();

  const isWorkerOnline = () => {
    if (!worker.lastHeartbeat) return false;
    const lastHeartbeat = new Date(worker.lastHeartbeat);
    const now = new Date();
    const diffInMinutes = (now.getTime() - lastHeartbeat.getTime()) / (1000 * 60);
    return diffInMinutes <= 15;
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '200px' }}>
            <FiberManualRecordIcon
              sx={{
                color: isWorkerOnline() ? 'success.main' : 'error.main',
                mr: 1
              }}
            />
            <Typography variant="subtitle2">{worker.workerId}</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
              {t('LAST_HEARTBEAT')}:
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {format(new Date(worker?.lastHeartbeat), 'dd/MM/yyyy HH:mm:ss')}
            </Typography>
            <Tooltip
              title={t('ADMIN_REVISION_WORKER_LAST_HEARTBEAT_TOOLTIP')}
              placement="top"
            >
              <IconButton size="small" sx={{ ml: 0.5 }}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
              {t('CPU_USAGE')}:
            </Typography>
            <Typography variant="caption" color="text.secondary" dir="ltr" sx={{ whiteSpace: 'nowrap' }}>
              {((worker?.metrics?.cpuUsage?.user + worker?.metrics?.cpuUsage?.system) / 1000000).toFixed(2)}%
            </Typography>
            <Tooltip
              title={t('ADMIN_REVISION_WORKER_CPU_USAGE_TOOLTIP')}
              placement="top"
            >
              <IconButton size="small" sx={{ ml: 0.5 }}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
              {t('MEMORY_USAGE')}:
            </Typography>
            <Typography variant="caption" color="text.secondary" dir="ltr" sx={{ whiteSpace: 'nowrap' }}>
              {(worker?.metrics?.memoryUsage?.heapUsed / 1024 / 1024).toFixed(2)} MB
            </Typography>
            <Tooltip
              title={t('ADMIN_REVISION_WORKER_MEMORY_USAGE_TOOLTIP')}
              placement="top"
            >
              <IconButton size="small" sx={{ ml: 0.5 }}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
              {t('UPTIME')}:
            </Typography>
            <Typography variant="caption" color="text.secondary" dir="ltr" sx={{ whiteSpace: 'nowrap' }}>
              {Math.floor(worker?.metrics?.uptime / 60)} minutes
            </Typography>
            <Tooltip
              title={t('ADMIN_REVISION_WORKER_UPTIME_TOOLTIP')}
              placement="top"
            >
              <IconButton size="small" sx={{ ml: 0.5 }}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const AdminJobs: React.FC = () => {
  const { t } = useTranslation();
  const tKill = t('ADMIN_REVISION_KILL', { defaultValue: 'Kill job' });
  const tKillSuccess = t('ADMIN_REVISION_KILL_SUCCESS', { defaultValue: 'Job killed successfully' });
  const tKillError = t('ADMIN_REVISION_KILL_ERROR', { defaultValue: 'Error killing job' });
  const navigate = useNavigate();
  const { setOrganizationId, auth } = useContext(AuthContext)
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortBy, setSortBy] = useState('startedAt');
  const [searchId, setSearchId] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [statusFilter, setStatusFilter] = useState<RevisionStatus | 'all'>('all');
  const { data, isLoading, error, refetch: refetchData } = useQuery({
    queryKey: ['jobs', page, rowsPerPage, sortOrder, sortBy, searchId, statusFilter],
    queryFn: () => getJobQueueData({
      page: page + 1,
      limit: rowsPerPage,
      sortBy,
      order: sortOrder,
      search: searchId.trim() === "" ? null : searchId,
      startDate: startDate ? startDate : null,
      endDate: endDate ? endDate : null,
      status: statusFilter === 'all' ? null : statusFilter
    })
  });
  const { data: workers, isLoading: isLoadingWorkerStats } = useQuery<Worker[]>({
    queryKey: ['workerStats'],
    queryFn: () => getWorkerStats()
  });
  const { data: jobAnalytics, isLoading: isLoadingJobAnalytics } = useQuery<JobAnalytics>({
    queryKey: ['jobAnalytics'],
    queryFn: () => getAllJobAnalytics(),
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const open = Boolean(anchorEl);
  const { enqueueSnackbar } = useSnackbar();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleSearch = () => {
    refetchData();
    setPage(0);
    setRowsPerPage(50);
    setSortBy('startedAt');
    setSortOrder('desc');
    setStatusFilter('all');
  }

  const toggleRow = (jobId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const columns: Column[] = [
    { id: 'id', title: t('ADMIN_REVISION_JOB_ID') },
    { id: 'status', title: t('ADMIN_REVISION_JOB_STATUS') },
    { id: 'workerId', title: t('ADMIN_REVISION_JOB_WORKER') },
    { id: 'fileName', title: t('ADMIN_REVISION_JOB_FILE') },
    { id: 'startedAt', title: t('ADMIN_REVISION_JOB_START') },
    { id: 'processedAt', title: t('ADMIN_REVISION_JOB_PROCESSING') },
    { id: 'endedAt', title: t('ADMIN_REVISION_JOB_END') },
    { id: 'timeJobWaiting', title: t('ADMIN_REVISION_JOB_WAITING_TIME') },
    { id: 'timeJobProcessing', title: t('ADMIN_REVISION_JOB_PROCESSING_TIME') },
  ];

  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: string) => {
    const isAsc = sortBy === newOrderBy && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(newOrderBy);
    setPage(0);
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string | null, showYear: boolean = false) => {
    if (!dateString) return '-';
    const formatString = showYear ? 'dd/MM/yyyy HH:mm' : 'dd/MM HH:mm';
    return format(new Date(dateString), formatString);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>, job: any) => {

    setAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedJob(null);
  };

  const handleViewRevision = (e: React.MouseEvent<unknown>, job: any) => {
    e.preventDefault();
    e.stopPropagation();

    localStorage.setItem('organizationId', job.organization.id);
    setOrganizationId(job.organization.id);

    if (job.status === 'resolving') {
      navigate(`/admin/business/${job.organization.id}/project/${job.project.id}/revision/${job.id}/${job.ticket.id}`);
    } else {
      navigate(`/admin/business/${job.organization.id}/project/${job.project.id}/board`);
    }
  };


  const handleDownloadFile = async (url: string, filename: string) => {
    let name = filename;
    try {
      if (filename.includes('output')) {
        const urlParts = url.split('/');
        const fileId = urlParts[urlParts.length - 3]
        name = `output_${fileId}.json`;
      }
      if (filename.includes('input')) {
        const urlParts = url.split('/');
        const fileId = urlParts[urlParts.length - 3]
        name = `input_${fileId}.json`;
      }
      if (filename.includes('inspection')) {
        const urlParts = url.split('/');
        const fileId = urlParts[urlParts.length - 3]
        name = `inspection_${fileId}.zip`;
      }
      const response = await fetch(`${url}?authorization=${auth?.token}`);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      enqueueSnackbar(t('ERROR_DOWNLOADING_FILE'), { variant: 'error' });
    }
  };

  const handleKillJob = async (job: any) => {
    try {
      await killRevision(job.id, job.project.id, job.organization.id);
      enqueueSnackbar(tKillSuccess, { variant: 'success' });
      handleClose();
      refetchData();
    } catch (error) {
      enqueueSnackbar(tKillError, { variant: 'error' });
    }
  };

  const getDownloadMenuItems = (job: any) => {
    const items = [];
    const canDownload = job.status === RevisionStatus.SUCCESS
    if (canDownload) {
      if (job.files?.full) {
        items.push(
          <a key="full" href={`${job.files.full.url}?authorization=${auth?.token}`} rel='noopener noreferrer' download className='dropdown-menu__link'>
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_DETAILED_REPORT')}
            </MenuItem>
          </a>
        );
      }
      if (job.files?.rating) {
        items.push(
          <a key="rating" href={`${job.files.rating.url}?authorization=${auth?.token}`} rel='noopener noreferrer' download className='dropdown-menu__link'>
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_RATING_REPORT')}
            </MenuItem>
          </a>
        );
      }
      if (job.files?.dxf) {
        items.push(
          <a key="dxf" href={`${job.files.dxf.url}?authorization=${auth?.token}`} rel='noopener noreferrer' download className='dropdown-menu__link'>
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_3D_REPORT')}
            </MenuItem>
          </a>
        );
      }
      if (job.files?.h_report) {
        items.push(
          <a key="h_report" href={`${job.files.h_report.url}?authorization=${auth?.token}`} rel='noopener noreferrer' download className='dropdown-menu__link'>
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_H_REPORT')}
            </MenuItem>
          </a>
        );
      }
      if (job.files?.report_1045) {
        items.push(
          <a key="report_1045" href={`${job.files.report_1045.url}?authorization=${auth?.token}`} rel='noopener noreferrer' download className='dropdown-menu__link'>
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_1045_REPORT')}
            </MenuItem>
          </a>
        );
      }
      if (job.files?.idf) {
        items.push(
          <a key="idf" href={`${job.files.idf.url}?authorization=${auth?.token}`} rel='noopener noreferrer' download className='dropdown-menu__link'>
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_IDF_FILE')}
            </MenuItem>
          </a>
        );
      }
      if (job.files?.mod_idf) {
        items.push(
          <a key="mod_idf" href={`${job.files.mod_idf.url}?authorization=${auth?.token}`} rel='noopener noreferrer' download className='dropdown-menu__link'>
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_RUN_FILE')}
            </MenuItem>
          </a>
        );
      }
      if (job.files?.output_json) {
        items.push(
          <MenuItem key="output_json" onClick={() => handleDownloadFile(job.files.output_json.url, 'output.json')}>
            {t('REVISION_MENU_DOWNLOAD_OUTPUT_JSON_FILE')}
          </MenuItem>
        );
      }
      if (job.files?.input_json) {
        items.push(
          <MenuItem key="input_json" onClick={() => handleDownloadFile(job.files.input_json.url, 'input.json')}>
            {t('REVISION_MENU_DOWNLOAD_INPUT_JSON_FILE')}
          </MenuItem>
        );
      }
      if (job.files?.h_report_csv) {
        items.push(
          <a key="h_report_csv" href={`${job.files.h_report_csv.url}?authorization=${auth?.token}`} rel='noopener noreferrer' download className='dropdown-menu__link'>
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_CSV_FILE')}
            </MenuItem>
          </a>
        );
      }
      if (job.files?.idf_epjson) {
        items.push(
          <MenuItem key="idf_epjson" onClick={() => handleDownloadFile(job.files.idf_epjson.url, 'inspection.zip')}>
            {t('REVISION_MENU_DOWNLOAD_EPJSON_FILE')}
          </MenuItem>
        );
      }
      if (job.files?.summary_project) {
        items.push(
          <a key="summary_project" href={`${job.files.summary_project.url}?authorization=${auth?.token}`} rel='noopener noreferrer' download className='dropdown-menu__link'>
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_SUMMARY_PROJECT')}
            </MenuItem>
          </a>
        );
      }
      if (job.files?.dsb) {
        items.push(
          <a key="dsb" href={`${job.files.dsb.url}?authorization=${auth?.token}`} rel='noopener noreferrer' download className='dropdown-menu__link'>
            <MenuItem>
              {t('REVISION_MENU_DOWNLOAD_DSB_MODEL_FILE')}
            </MenuItem>
          </a>
        );
      }
      items.push(
        <MenuItem
          onClick={async () => {
            const { url } = await generateViewerUrl(job.id);
            handleClose();
            if (!url) {
              enqueueSnackbar(t('REVISION_MENU_SHARE_EPJSON_FILE_ERROR'), { variant: 'error' });
            } else {
              window.open(url, '_blank');

              enqueueSnackbar(t('REVISION_MENU_SHARE_EPJSON_FILE_SUCCESS'), { variant: 'success' });
            }
          }}
        >
          {t('REVISION_MENU_SHARE_EPJSON_FILE')}
        </MenuItem>
      );

      return items;
    };
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };




  if (error) return <div>Erreur: {String(error)}</div>;

  const jobs = data?.data || [];
  const pagination = data?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  };
  const pendingCount = jobAnalytics?.pendingCount || 0;
  const processingCount = jobAnalytics?.processingCount || 0;
  const ticketCount = jobAnalytics?.ticketCount || 0;
  const totalJobsCount = jobAnalytics?.totalJobsCount || 0;



  return (
    <Box >
      <Grid container spacing={3}>
        <Grid item xs={12}>

          {isLoadingWorkerStats ? (
            <CircularProgress />
          ) : (
            <Grid container spacing={0} direction="column">
              {workers?.slice(0, 5).map((worker: Worker) => (
                <Grid item xs={12} key={worker.workerId}>
                  <WorkerCard worker={worker} />
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('ADMIN_REVISION_PENDING_JOBS')}
              </Typography>
              <Typography variant="h4">
                {formatNumber(pendingCount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('ADMIN_REVISION_PROCESSING_JOBS')}
              </Typography>
              <Typography variant="h4">
                {formatNumber(processingCount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('ADMIN_REVISION_ACTIVE_TICKETS')}
              </Typography>
              <Typography variant="h4">
                {formatNumber(ticketCount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('ADMIN_REVISION_TOTAL_JOBS')}
              </Typography>
              <Typography variant="h4">
                {formatNumber(totalJobsCount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={3} xs={12} mt={1} mb={2} >
        <Grid item xs={12} sm={6} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: '24px',
              lineHeight: '31px',
              color: '#2F3B49',
            }}
          >
            {t('ADMIN_REVISION_TITLE')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t('ADMIN_REVISION_SEARCH_JOB_ID')}
            name="id"
            value={searchId}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                height: '50px'
              }
            }}
            onChange={(e) => setSearchId(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl sx={{ width: '100%' }}>
            <InputLabel>{t('ADMIN_REVISION_JOB_STATUS')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('ADMIN_REVISION_JOB_STATUS')}
              onChange={(e) => setStatusFilter(e.target.value as RevisionStatus | 'all')}
              sx={{
                height: '50px'
              }}
            >
              <MenuItem value="all">{t('ALL STATUS')}</MenuItem>
              <MenuItem value="pending">{t('PENDING')}</MenuItem>
              <MenuItem value="processing">{t('PROCESSING')}</MenuItem>
              <MenuItem value="success">{t('SUCCESS')}</MenuItem>
              <MenuItem value="failed">{t('FAILED')}</MenuItem>
              <MenuItem value="resolving">{t('RESOLVING')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <CustomDatePicker
            label={t('ADMIN_REVISION_START_DATE')}
            value={startDate}
            onChange={setStartDate}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <CustomDatePicker
            label={t('ADMIN_REVISION_END_DATE')}
            value={endDate}
            onChange={setEndDate}
            minDate={startDate}
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <ButtonCustom
            size='medium'
            variant="contained"
            color="primary"
            onClick={handleSearch}
            sx={{ height: '50px' }}
          >
            {t('ADMIN_REVISION_SEARCH')}
          </ButtonCustom>
        </Grid>
      </Grid>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
              <CustomTableFontCell />
              {columns.map((column) => (
                <CustomTableFontCell key={column.id} dir={column.id === 'fileName' ? 'ltr' : undefined}>
                  <StyledTableSortLabel
                    active={sortBy === column.id}
                    direction={sortBy === column.id ? sortOrder : 'asc'}
                    onClick={(event) => handleRequestSort(event, column.id)}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {column.title}
                    </Typography>
                    {sortBy === column.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortOrder === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
              ))}
              <CustomTableCell sx={{ cursor: 'default !important' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <React.Fragment key={job.id}>
                <TableRow
                  onClick={(e) => {
                    if (e.target === e.currentTarget || e.target instanceof HTMLTableCellElement) {
                      handleViewRevision(e, job);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      border: 'none',
                    },
                  }}
                >
                  <CustomTableCell
                    style={{ paddingLeft: "0px", paddingRight: "0px" }}
                    sx={{ borderBottom: expandedRows.has(job.id) ? 'none' : undefined }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRow(job.id);
                      }}
                    >
                      {expandedRows.has(job.id) ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </CustomTableCell>
                  {columns.map((column) => (
                    <CustomTableCell
                      sx={{
                        cursor: 'default !important',
                        borderBottom: expandedRows.has(job.id) ? 'none' : undefined,
                        userSelect: 'text'
                      }}
                      key={`${job.id}-${column.id}`}
                      dir={column.id === 'fileName' ? 'ltr' : undefined}
                    >
                      <Typography variant="body2">
                        {column.id === 'status' ? (
                          <StatusChip status={job[column.id]} />
                        ) : ['startedAt', 'processedAt', 'endedAt'].includes(column.id) ? (
                          formatDate(job[column.id], column.id === 'startedAt')
                        ) : (
                          job[column.id]
                        )}
                      </Typography>
                    </CustomTableCell>
                  ))}
                  <CustomTableCell sx={{ borderBottom: expandedRows.has(job.id) ? 'none' : undefined }}>
                    <ButtonIcon
                      isActive={selectedJob?.id === job.id ? true : false}
                      onClick={(e) => handleClick(e, job)}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <MoreHorizIcon />
                    </ButtonIcon>
                    <Menu
                      sx={{ fontSize: 14 }}
                      anchorEl={anchorEl}
                      open={open && selectedJob?.id === job.id}
                      onClose={handleClose}
                    >
                      <MenuItem onClick={(e) => handleViewRevision(e, job)}>
                        {t('ADMIN_REVISION_VIEW')}
                      </MenuItem>
                      {getDownloadMenuItems(job)}
                      {job.status === RevisionStatus.PROCESSING && (
                        <MenuItem onClick={() => handleKillJob(job)}>
                          {tKill}
                        </MenuItem>
                      )}
                    </Menu>
                  </CustomTableCell>
                </TableRow>
                <TableRow key={`collapse-row-${job.id}`}>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length + 2} sx={{ border: 'none' }}>
                    <Collapse in={expandedRows.has(job.id)} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Section des IDs */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('ORGANIZATION ID')}:
                            </Typography>
                            <Typography variant="body2">
                              {job.organization?.id}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('PROJECT ID')}:
                            </Typography>
                            <Typography variant="body2">
                              {job.project?.id}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('REVISION ID')}:
                            </Typography>
                            <Typography variant="body2">
                              {job.id}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Section des noms et images */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                            <Typography variant="body2" color="text.secondary">
                              {t('ORGANIZATION')}:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {job.organization?.image_url && (
                                <Box
                                  component="img"
                                  src={job.organization.image_url}
                                  alt={job.organization.name}
                                  sx={{ width: 24, height: 24, borderRadius: 0.5 }}
                                />
                              )}
                              <Typography variant="body2">
                                {job.organization?.name}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {job.project?.image_url && (
                              <Box
                                component="img"
                                src={job.project.image_url}
                                alt={job.project.name}
                                sx={{ width: 24, height: 24, borderRadius: 0.5 }}
                              />
                            )}
                            <Typography variant="body2" color="text.secondary">
                              {t('PROJECT')}:
                            </Typography>
                            <Typography variant="body2">
                              {job.project?.name}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {job.user?.image && (
                              <Box
                                component="img"
                                src={job.user.image}
                                alt={job.user.name}
                                sx={{ width: 24, height: 24, borderRadius: '50%' }}
                              />
                            )}
                            {job.user?.name && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {t('USER')}:
                              </Typography>
                              <Typography variant="body2">
                                {job.user?.name}
                              </Typography>
                            </Box>
                            }
                            {job.user?.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {t('USER EMAIL')}:
                              </Typography>
                              <Typography variant="body2">
                                {job.user?.email}
                              </Typography>
                            </Box>
                            }
                            {job.user?.phone &&
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('USER PHONE')}:
                                </Typography>
                                <Typography variant="body2">
                                  {job.user?.phone}
                                </Typography>
                              </Box>
                            }
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[50, 100, 200]}
          component="div"
          count={pagination.totalItems}
          rowsPerPage={pagination.itemsPerPage}
          page={pagination.currentPage - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </TableContainer>
    </Box>
  );
};

export default AdminJobs;